import {
  Button,
  ConfirmModal,
  Flex,
  Loading,
  Table,
  TableRowMenuCell,
  type ColumnsType as _ColumnsType,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useMessage } from '@/providers';
import {
  TABLE_DATETIME_WIDTH,
  TABLE_DATE_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  dateFormat,
  datetimeFormat,
  isRS,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useBudgetTypes,
  useCreateBudgetType,
  useDeleteBudgetType,
  useUpdateBudgetType,
} from '../../hooks';
import {
  BudgetTypeLabelModifier,
  type BudgetTypeModel,
  type BudgetTypesModel,
} from '../../models';
import { DEFAULT_PAGINATION_SETTING } from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { BudgetTypeFormDrawer } from './BudgetTypeFormDrawer';
import { BudgetTypeSearchForm } from './BudgetTypeSearchForm';
import { BudgetTypeSortableTable } from './BudgetTypeSortableTable';

type ColumnsType = _ColumnsType<z.infer<typeof BudgetTypesModel.element>>;

export const BudgetTypesPage: FC = () => {
  const { t } = useTranslation();
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const [searchParams] = useSearchParams();
  const currentFiscalYear = useCurrentFiscalYear();

  const searchParamsFiscalYear =
    searchParams.get('fiscalYear') ??
    currentFiscalYear.data?.toString() ??
    currentFiscalYear.basicFiscalYear.toString();

  const budgetTypes = useBudgetTypes({
    params: {
      fiscalYear: searchParamsFiscalYear.toString(),
    },
  });
  const { mutateAsync: createMutateAsync } = useCreateBudgetType();
  const { mutateAsync: updateMutateAsync } = useUpdateBudgetType();
  const { mutateAsync: deleteMutateAsync } = useDeleteBudgetType();
  const editPermission = useMasterTablePermission(true);
  const [budgetTypeDelete, setBudgetTypeDelete] = useState<z.infer<
    typeof BudgetTypeModel
  > | null>(null);

  const columns: ColumnsType = useMemo(
    () => [
      {
        title: t('fiscal-year'),
        key: 'fiscalYear',
        dataIndex: 'fiscalYear',
        width: TABLE_YEAR_WIDTH,
      },
      {
        title: t('budget-type.master-table-title-budget-type'),
        key: 'type',
        dataIndex: 'type',
        width: 120,
        render: (value) => BudgetTypeLabelModifier[value],
      },
      {
        title: t('budget-type.master-table-title-budget-name'),
        key: 'name',
        dataIndex: 'name',
      },
      ...((isRS()
        ? []
        : [
            {
              title: t('budget-type-external-system-key'),
              dataIndex: 'externalSystemKey',
            },
            {
              title: t('scheduled-date-of-decision'),
              dataIndex: 'scheduledDateOfDecision',
              width: TABLE_DATE_WIDTH,
              render: (datetime) => dateFormat({ datetime }),
            },
          ]) satisfies ColumnsType),
      {
        title: t('created-datetime'),
        key: 'createdAt',
        dataIndex: 'createdAt',
        width: TABLE_DATETIME_WIDTH,
        render: (datetime) => datetimeFormat({ datetime }),
      },
      {
        title: t('updated-datetime'),
        key: 'updatedAt',
        dataIndex: 'updatedAt',
        width: TABLE_DATETIME_WIDTH,
        render: (datetime) => datetimeFormat({ datetime }),
      },
      ...(!isRS() && editPermission.data && !isEditingOrder
        ? ([
            {
              title: '',
              key: 'actions',
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TableRowMenuCell
                  onEdit={() => {
                    setDrawerState({
                      open: true,
                      type: 'budget-type',
                      isEditing: true,
                      id: record.id,
                    });
                  }}
                  items={[
                    {
                      key: 'delete',
                      label: t('delete'),
                      danger: true,
                      onClick: () => setBudgetTypeDelete(record),
                    },
                  ]}
                />
              ),
            },
          ] satisfies ColumnsType)
        : []),
    ],
    [editPermission.data, setDrawerState, t, isEditingOrder],
  );

  useEffect(() => {
    if (drawerState.open) {
      setIsEditingOrder(false);
    }
  }, [drawerState]);

  if (budgetTypes.isPending || !budgetTypes.data) {
    return <Loading />;
  }

  const selected = budgetTypes.data.find(
    (d) =>
      drawerState.open &&
      ['budget-type', 'decided-budget'].includes(drawerState.type) &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  return (
    <>
      <BudgetTypeSearchForm />
      {editPermission.data &&
        !isEditingOrder &&
        searchParamsFiscalYear &&
        searchParamsFiscalYear !== 'all' && (
          <Flex justify="flex-end" style={{ marginBottom: 12 }}>
            <Button
              onClick={() => {
                setIsEditingOrder((prev) => !prev);
              }}
            >
              {t('sort-order-edit')}
            </Button>
          </Flex>
        )}

      {isEditingOrder ? (
        <BudgetTypeSortableTable
          dataSource={budgetTypes.data}
          columns={columns}
          onCancel={() => {
            setIsEditingOrder(false);
          }}
          onSubmitSuccess={() => {
            setIsEditingOrder(false);
          }}
        />
      ) : (
        <Table
          dataSource={budgetTypes.data}
          rowKey="id"
          columns={columns}
          pagination={
            searchParamsFiscalYear === 'all'
              ? DEFAULT_PAGINATION_SETTING
              : false
          }
        />
      )}

      <BudgetTypeFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                fiscalYear: selected?.fiscalYear
                  ? dayjs().year(selected.fiscalYear)
                  : undefined,
                scheduledDateOfDecision: selected?.scheduledDateOfDecision
                  ? dayjs(selected.scheduledDateOfDecision)
                  : undefined,
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          if (!drawerState.open) {
            return null;
          }
          drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);
          await openMessage(
            'success',
            drawerState.isEditing
              ? t('message-update-completed')
              : t('message-create-completed'),
          );
          await budgetTypes.refetch();
          setDrawerState({ open: false });
        }}
      />
      <ConfirmModal
        open={budgetTypeDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(budgetTypeDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(budgetTypeDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              setBudgetTypeDelete(null),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setBudgetTypeDelete(null)}
        skipCheck
      />
    </>
  );
};
